import { useEffect, useRef, useCallback } from "react";
import {
  MA_MD_DEBUG_PREFIX,
  buildMACusipTopicPath,
  diffCusipSets,
  normalizeCusip,
  normalizeCusipList,
  describeCusipCollection,
} from "./maInventoryMarketDataUtils";
import { insertFrontendLog } from "../http/api";

// Activation is confirmed by the STOMP RECEIPT frame the backend sends for each
// SUBSCRIBE (first topic message remains as a fallback for older backends). The
// timeout is deliberately generous and retries are capped: a briefly-slow backend
// must not trigger an amplifying retry storm (doc §2.4/§5).
const RETRY_DELAYS = [2000, 5000, 10000];
const SUBSCRIBE_MESSAGE_TIMEOUT_MS = 10000;

// Live subscriptions are established in chunks across frames so a large watchlist
// doesn't fire thousands of subscribes (and their timers) in one blocking task.
// The first chunk runs synchronously, so small watchlists subscribe immediately.
const SUBSCRIBE_CHUNK_SIZE = 250;

// Per-CUSIP console logs and audit POSTs are diagnostic and scale with message /
// subscription volume; at thousands of rows they dominate the hot paths. Keep
// them behind a debug flag (opt in via localStorage MA_MD_DEBUG="true", read once
// at load); errors, warnings and per-reconcile summaries stay unconditional.
const MA_MD_DEBUG =
  typeof window !== "undefined" &&
  (() => {
    try {
      return window.localStorage?.getItem("MA_MD_DEBUG") === "true";
    } catch (error) {
      return false;
    }
  })();

const debugLog = (...args) => {
  if (MA_MD_DEBUG) console.log(...args);
};

// ─── [MA-TIMING] TEMPORARY DIAGNOSTIC ─────────────────────────────────────────
// Records the precise per-CUSIP timeline (subscribe → first message → timeout) so
// we can tell whether a timed-out topic EVER receives a message (missed-ACK race)
// or only receives it late (5s timer too aggressive). Per-CUSIP, so keep OFF for
// large watchlists (thousands of lines skews the very perf it measures); flip on
// only for small-watchlist diagnosis. REMOVE once no longer needed.
const MA_TIMING_DIAG = false;
const nowMs = () =>
  (typeof performance !== "undefined" && performance.now ? performance.now() : Date.now());
const timingLog = (...args) => {
  if (MA_TIMING_DIAG) console.log("[MA-TIMING]", ...args);
};
// ──────────────────────────────────────────────────────────────────────────────

// Invoke rIC/rAF as window methods — calling them detached ("const r = window.
// requestAnimationFrame; r(cb)") throws "Illegal invocation" in real browsers.
const scheduleFrame = (callback) => {
  if (typeof window === "undefined") return setTimeout(callback, 0);
  if (typeof window.requestIdleCallback === "function") {
    // The timeout bounds idle starvation: during a watchlist open the main thread
    // is busy rendering the table and applying ticks, so untimed rIC can leave
    // later chunks waiting for seconds. With it, a full 5k pump completes in
    // ≤ ~(chunks × 100ms) even under load.
    return window.requestIdleCallback(callback, { timeout: 100 });
  }
  if (typeof window.requestAnimationFrame === "function") {
    return window.requestAnimationFrame(callback);
  }
  return setTimeout(callback, 0);
};

export const useMAInventorySubscriptions = ({
  client,
  connectionState,
  desiredCusips = [],
  instanceId = "default",
}) => {
  const maInventoryDesiredCusipsRef = useRef(new Set());
  const maInventorySubscriptionsRef = useRef(new Map());
  const maInventoryReconcileRunRef = useRef(0);

  const clientRef = useRef(client);
  useEffect(() => {
    clientRef.current = client;
  }, [client]);

  const logMAInventoryState = useCallback((stage, extra = {}) => {
    if (!MA_MD_DEBUG) return;
    // Log counts, not the full CUSIP arrays — those are ~50 KB at 5k rows.
    console.log(`${MA_MD_DEBUG_PREFIX} ${stage}`, {
      instanceId,
      connectionState: clientRef.current?.connected ? "connected" : connectionState,
      desiredCount: maInventoryDesiredCusipsRef.current.size,
      activeCount: maInventorySubscriptionsRef.current.size,
      ...extra,
    });
  }, [connectionState, instanceId]);

  const unsubscribeMACusip = useCallback((cusip, reason = "reconcile") => {
    const normalizedCusip = normalizeCusip(cusip);
    if (!normalizedCusip) return false;

    const entry = maInventorySubscriptionsRef.current.get(normalizedCusip);
    if (!entry) {
      debugLog(`${MA_MD_DEBUG_PREFIX} UNSUBSCRIBE_SKIPPED cusip=${normalizedCusip} reason=${reason} not-active/pending`);
      return false;
    }

    try {
      if (entry.timeoutId) {
        clearTimeout(entry.timeoutId);
      }
      if (Array.isArray(entry.subscriptions)) {
        entry.subscriptions.forEach((subscription) => {
          if (subscription?.unsubscribe) {
            subscription.unsubscribe();
          }
        });
      } else if (entry.subscription?.unsubscribe) {
        entry.subscription.unsubscribe();
      }
      debugLog(`${MA_MD_DEBUG_PREFIX} UNSUBSCRIBED cusip=${normalizedCusip} path=${entry.path} reason=${reason}`);
    } catch (error) {
      console.error(`${MA_MD_DEBUG_PREFIX} UNSUBSCRIBE_ERROR cusip=${normalizedCusip} path=${entry.path} reason=${reason}`, error);
    } finally {
      maInventorySubscriptionsRef.current.delete(normalizedCusip);
    }

    return true;
  }, []);

  const clearMAInventorySubscriptions = useCallback((reason = "clear-all") => {
    // Bump the run id so any in-flight chunked subscribe pump aborts — otherwise
    // a clear (desired-empty / disconnect) mid-burst would be followed by the
    // remaining chunks re-subscribing CUSIPs nobody wants anymore.
    maInventoryReconcileRunRef.current += 1;

    const activeCusips = Array.from(maInventorySubscriptionsRef.current.keys());
    if (!activeCusips.length) {
      debugLog(`${MA_MD_DEBUG_PREFIX} CLEAR_SKIPPED reason=${reason} already-empty`);
      return;
    }

    console.log(`${MA_MD_DEBUG_PREFIX} CLEAR_ALL reason=${reason} count=${activeCusips.length} cusips=${describeCusipCollection(activeCusips)}`);
    activeCusips.forEach((cusip) => unsubscribeMACusip(cusip, reason));
  }, [unsubscribeMACusip]);

  const stateRef = useRef({
    subscribeMACusip: null,
    handleSubscriptionFailed: null,
    reconcileMAInventorySubscriptions: null,
  });

  const markSubscriptionActive = useCallback((cusip, generationId, reason) => {
    const entry = maInventorySubscriptionsRef.current.get(cusip);
    if (!entry || entry.generationId !== generationId) return;
    if (entry.status === "active") return;

    if (entry.timeoutId) {
      clearTimeout(entry.timeoutId);
      entry.timeoutId = null;
    }

    entry.status = "active";
    debugLog(`${MA_MD_DEBUG_PREFIX} SUBSCRIBE_ACTIVE cusip=${cusip} reason=${reason}`);
  }, []);

  // Fallback activation path: any first message on the topic (a market-data
  // snapshot, or the legacy "ACK:" broadcast from an older backend) confirms the
  // subscription when no RECEIPT frame was received.
  const handleSubscriptionMessageReceived = useCallback((cusip, generationId) => {
    markSubscriptionActive(cusip, generationId, "first-message");
  }, [markSubscriptionActive]);

  const handleSubscriptionFailed = useCallback((cusip, generationId) => {
    const entry = maInventorySubscriptionsRef.current.get(cusip);
    if (!entry || entry.generationId !== generationId) return;

    if (entry.timeoutId) {
      clearTimeout(entry.timeoutId);
      entry.timeoutId = null;
    }
    
    entry.status = "failed";
    const attempt = entry.attempt || 1;
    
    console.warn(`${MA_MD_DEBUG_PREFIX} SUBSCRIBE_FAILED cusip=${cusip} attempt=${attempt}`);
    
    if (attempt <= RETRY_DELAYS.length) {
      const delay = RETRY_DELAYS[attempt - 1];
      console.log(`${MA_MD_DEBUG_PREFIX} SUBSCRIBE_RETRY_SCHEDULED cusip=${cusip} delay=${delay}ms attempt=${attempt + 1}`);
      setTimeout(() => {
        if (!maInventoryDesiredCusipsRef.current.has(cusip)) {
           console.log(`${MA_MD_DEBUG_PREFIX} RETRY_CANCELLED cusip=${cusip} (no longer desired)`);
           maInventorySubscriptionsRef.current.delete(cusip);
           return;
        }
        if (clientRef.current?.connected && stateRef.current.subscribeMACusip) {
           stateRef.current.subscribeMACusip(cusip, "retry", attempt + 1);
        }
      }, delay);
    } else {
      console.error(`${MA_MD_DEBUG_PREFIX} RETRY_LIMIT_REACHED cusip=${cusip} failed after ${attempt} attempts`);
    }
  }, []);

  stateRef.current.handleSubscriptionFailed = handleSubscriptionFailed;

  const handleSubscriptionTimeout = useCallback((cusip, generationId) => {
    // [MA-TIMING] TEMP: the decisive line — at timeout, did this topic EVER get a message?
    const timingEntry = maInventorySubscriptionsRef.current.get(cusip);
    const dt = timingEntry?.tSubscribe ? Math.round(nowMs() - timingEntry.tSubscribe) : -1;
    timingLog(
      `TIMEOUT cusip=${cusip} dtSinceSubscribe=${dt}ms everReceivedMsg=${!!timingEntry?.tFirstMsg} msgCount=${timingEntry?.msgCount || 0}`
    );
    console.warn(`${MA_MD_DEBUG_PREFIX} SUBSCRIBE_TIMEOUT cusip=${cusip}`);
    if (stateRef.current.handleSubscriptionFailed) {
      stateRef.current.handleSubscriptionFailed(cusip, generationId);
    }
  }, []);

  const subscribeMACusip = useCallback((cusip, reason = "reconcile", attempt = 1) => {
    const normalizedCusip = normalizeCusip(cusip);
    if (!normalizedCusip) return false;

    const stompClient = clientRef.current;
    if (!stompClient?.connected) {
      debugLog(`${MA_MD_DEBUG_PREFIX} SUBSCRIBE_DEFERRED cusip=${normalizedCusip} reason=${reason} client-not-connected`);
      return false;
    }

    const path = buildMACusipTopicPath(normalizedCusip);
    let entry = maInventorySubscriptionsRef.current.get(normalizedCusip);
    
    if (entry && entry.status === "active") {
      debugLog(`${MA_MD_DEBUG_PREFIX} SUBSCRIBE_SKIPPED cusip=${normalizedCusip} reason=${reason} already-active`);
      return false;
    }
    if (entry && entry.status === "pending") {
      debugLog(`${MA_MD_DEBUG_PREFIX} SUBSCRIBE_SKIPPED cusip=${normalizedCusip} reason=${reason} already-pending`);
      return false;
    }

    // If a previous attempt exists in failed/unknown state, clean it up before retrying.
    const existingSubscriptions = Array.isArray(entry?.subscriptions)
      ? entry.subscriptions
      : entry?.subscription
        ? [entry.subscription]
        : [];

    if (existingSubscriptions.length) {
      existingSubscriptions.forEach((subscription) => {
        try {
          if (subscription?.unsubscribe) {
            subscription.unsubscribe();
          }
        } catch (error) {
          console.warn(`${MA_MD_DEBUG_PREFIX} SUBSCRIBE_REPLACE_UNSUBSCRIBE_ERROR cusip=${normalizedCusip}`, error);
        }
      });
    }

    const currentRunId = maInventoryReconcileRunRef.current;
    debugLog(`${MA_MD_DEBUG_PREFIX} SUBSCRIBE_START cusip=${normalizedCusip} path=${path} reason=${reason} attempt=${attempt}`);

    const timestamp = Date.now();
    const subId = `sub-ma-${normalizedCusip}-${timestamp}-${attempt}`;
    const receiptId = `receipt-ma-${normalizedCusip}-${timestamp}-${attempt}`;

    if (entry && entry.timeoutId) {
      clearTimeout(entry.timeoutId);
    }

    const timeoutId = setTimeout(() => {
      handleSubscriptionTimeout(normalizedCusip, currentRunId);
    }, SUBSCRIBE_MESSAGE_TIMEOUT_MS);

    entry = {
      cusip: normalizedCusip,
      path,
      status: "pending",
      subscription: null,
      subscriptions: [],
      attempt,
      generationId: currentRunId,
      timeoutId,
      // [MA-TIMING] TEMP
      tSubscribe: nowMs(),
      tFirstMsg: null,
      msgCount: 0,
    };
    maInventorySubscriptionsRef.current.set(normalizedCusip, entry);
    timingLog(`SUBSCRIBE cusip=${normalizedCusip} attempt=${attempt} t=${Math.round(entry.tSubscribe)}ms`);

    try {
      // Primary activation path: the backend answers each SUBSCRIBE with a STOMP
      // RECEIPT frame (one direct frame to this session — no broker fan-out).
      // Registered before subscribe() so the receipt can't arrive unobserved.
      if (typeof stompClient.watchForReceipt === "function") {
        stompClient.watchForReceipt(receiptId, () => {
          const timingEntry = maInventorySubscriptionsRef.current.get(normalizedCusip);
          if (timingEntry?.tSubscribe) {
            timingLog(
              `RECEIPT cusip=${normalizedCusip} dtSinceSubscribe=${Math.round(nowMs() - timingEntry.tSubscribe)}ms`
            );
          }
          markSubscriptionActive(normalizedCusip, currentRunId, "receipt");
        });
      }

      const subscription = stompClient.subscribe(path, (message) => {
        // [MA-TIMING] TEMP: record if/when a message EVER lands on this topic,
        // relative to when we subscribed — fires even for messages arriving after
        // the 5s timeout (the callback stays live until we unsubscribe).
        const timingEntry = maInventorySubscriptionsRef.current.get(normalizedCusip);
        if (timingEntry) {
          timingEntry.msgCount = (timingEntry.msgCount || 0) + 1;
          if (!timingEntry.tFirstMsg) {
            timingEntry.tFirstMsg = nowMs();
            const dt = timingEntry.tSubscribe
              ? Math.round(timingEntry.tFirstMsg - timingEntry.tSubscribe)
              : -1;
            const ackLike =
              typeof message?.body === "string"
              && message.body.trim().toUpperCase().startsWith("ACK:");
            timingLog(
              `FIRST_MSG cusip=${normalizedCusip} dtSinceSubscribe=${dt}ms afterTimeout=${dt > SUBSCRIBE_MESSAGE_TIMEOUT_MS} ackLike=${ackLike}`
            );
          }
        }

        handleSubscriptionMessageReceived(normalizedCusip, currentRunId);

        let payload = message.body;
        try {
          payload = JSON.parse(message.body);
        } catch (error) {}

        debugLog(`${MA_MD_DEBUG_PREFIX} MESSAGE cusip=${normalizedCusip} path=${path}`, payload);
        window.dispatchEvent(
          new CustomEvent("maInventoryMarketDataUpdate", {
            detail: { cusip: normalizedCusip, path, payload },
          })
        );
      }, {
        id: subId,
        receipt: receiptId,
      });

      entry.subscriptions = [subscription];
      entry.subscription = subscription;

      debugLog(`${MA_MD_DEBUG_PREFIX} SUBSCRIBED_PATH cusip=${normalizedCusip} path=${path} subId=${subId}`);

      return true;
    } catch (error) {
      console.error(`${MA_MD_DEBUG_PREFIX} SUBSCRIBE_ERROR cusip=${normalizedCusip} path=${path} reason=${reason}`, error);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (stateRef.current.handleSubscriptionFailed) {
        stateRef.current.handleSubscriptionFailed(normalizedCusip, currentRunId);
      }
      return false;
    }
  }, [handleSubscriptionMessageReceived, handleSubscriptionTimeout, markSubscriptionActive]);

  stateRef.current.subscribeMACusip = subscribeMACusip;

  // Subscribe to a list of CUSIPs in chunks. The first chunk runs synchronously
  // (so small watchlists behave exactly as before); the rest are spread across
  // frames. A superseding reconcile bumps the run id and any pending chunk from
  // an older run aborts itself, so a mid-burst watchlist switch can't leak subs.
  const scheduleChunkedSubscribe = useCallback((cusips, reason, runId, onComplete) => {
    if (!cusips.length) {
      if (onComplete) onComplete();
      return;
    }

    let index = 0;
    const pump = () => {
      if (maInventoryReconcileRunRef.current !== runId) return; // superseded
      const slice = cusips.slice(index, index + SUBSCRIBE_CHUNK_SIZE);
      slice.forEach((cusip) => subscribeMACusip(cusip, `${reason}:visible`, 1));
      index += SUBSCRIBE_CHUNK_SIZE;
      if (index < cusips.length) {
        scheduleFrame(pump);
      } else if (onComplete) {
        onComplete();
      }
    };

    pump();
  }, [subscribeMACusip]);

  const reconcileMAInventorySubscriptions = useCallback((reason = "reconcile") => {
    const desiredSet = maInventoryDesiredCusipsRef.current;
    const reconcileRunId = ++maInventoryReconcileRunRef.current;
    const stompClient = clientRef.current;

    if (!stompClient?.connected) {
      debugLog(
        `${MA_MD_DEBUG_PREFIX} RECONCILE_DEFERRED reason=${reason} desiredCount=${desiredSet.size} client-not-connected`
      );
      return;
    }

    const activeSet = new Set(maInventorySubscriptionsRef.current.keys());
    const { toSubscribe, toUnsubscribe, unchanged } = diffCusipSets(activeSet, desiredSet);

    console.log(
      `${MA_MD_DEBUG_PREFIX} RECONCILE_START run=${reconcileRunId} reason=${reason} desired=${desiredSet.size} active=${activeSet.size} toSubscribe=${toSubscribe.length} toUnsubscribe=${toUnsubscribe.length} unchanged=${unchanged.length}`
    );

    // One aggregated audit log per reconcile — replaces the former per-CUSIP POSTs
    // (subscribe-requested / unsubscribed / removed / active), which flooded the
    // logging endpoint with thousands of requests on open/switch at scale.
    // No-op reconciles (e.g. a page turn re-prioritising an already-subscribed
    // list) are not audit-worthy and must not cost a network call.
    if (toSubscribe.length || toUnsubscribe.length) {
      insertFrontendLog({
        socketEvent: "maInventoryReconcile",
        reason,
        desired: desiredSet.size,
        subscribed: toSubscribe.length,
        unsubscribed: toUnsubscribe.length,
        unchanged: unchanged.length,
      });
    }

    toUnsubscribe.forEach((cusip) => unsubscribeMACusip(cusip, `${reason}:not-visible`));

    // Chunked subscribe settles over several frames, so the completeness check
    // runs on completion (not synchronously) to avoid false RECONCILE_MISMATCH.
    scheduleChunkedSubscribe(toSubscribe, reason, reconcileRunId, () => {
      if (maInventoryReconcileRunRef.current !== reconcileRunId) return; // superseded
      const remainingSet = new Set(maInventorySubscriptionsRef.current.keys());
      const postDiff = diffCusipSets(remainingSet, desiredSet);

      if (postDiff.toSubscribe.length || postDiff.toUnsubscribe.length) {
        console.warn(`${MA_MD_DEBUG_PREFIX} RECONCILE_MISMATCH run=${reconcileRunId} reason=${reason}`, {
          missingSubscriptions: postDiff.toSubscribe.length,
          staleSubscriptions: postDiff.toUnsubscribe.length,
        });
      } else {
        console.log(`${MA_MD_DEBUG_PREFIX} RECONCILE_OK run=${reconcileRunId} reason=${reason} count=${remainingSet.size}`);
      }

      logMAInventoryState("RECONCILE_COMPLETE", { reason, run: reconcileRunId });
    });
  }, [logMAInventoryState, scheduleChunkedSubscribe, unsubscribeMACusip]);

  stateRef.current.reconcileMAInventorySubscriptions = reconcileMAInventorySubscriptions;

  useEffect(() => {
    const normalizedDesiredCusips = normalizeCusipList(desiredCusips);
    maInventoryDesiredCusipsRef.current = new Set(normalizedDesiredCusips);

    console.log(
      `${MA_MD_DEBUG_PREFIX} DESIRED_SET_UPDATED count=${normalizedDesiredCusips.length} cusips=${describeCusipCollection(normalizedDesiredCusips)}`
    );

    if (!normalizedDesiredCusips.length) {
      clearMAInventorySubscriptions("desired-empty");
      return;
    }

    reconcileMAInventorySubscriptions("desired-set-update");
  }, [clearMAInventorySubscriptions, desiredCusips, reconcileMAInventorySubscriptions]);

  useEffect(() => {
    if (connectionState === "connected") {
      reconcileMAInventorySubscriptions("connection-state-connected");
    } else if (connectionState === "disconnected") {
      clearMAInventorySubscriptions("connection-disconnected");
    }
  }, [connectionState, reconcileMAInventorySubscriptions, clearMAInventorySubscriptions]);

  return {
    clearMAInventorySubscriptions,
  };
};

export default useMAInventorySubscriptions;
